import { Injectable, computed, inject, signal } from "@angular/core";
import { Util } from "@auvious/common";
import { IEndpoint } from "@auvious/rtc";
import {
  Subject,
  filter,
  firstValueFrom,
  map,
  of,
  takeUntil,
  timeout,
  timer
} from "rxjs";

import {
  AIDeepfakeEvent,
  DeepfakeFailureReasonEnum,
  DeepfakeStatusEnum,
  UserRoleEnum
} from "../core-ui.enums";
import {
  AgentParam,
  DEEPFAKE_CHECK_MODES,
  DEEPFAKE_PENDING_STATES,
  DEFAULT_DEEPFAKE_CHECK_MODE,
  DEFAULT_DEEPFAKE_CLIP_SECONDS,
  DEFAULT_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS,
  DeepfakeCheckKind,
  DeepfakeCheckMode,
  DeepfakeTrigger,
  EndpointStateEnum,
  IDeepfakeCheck,
  IDeepfakeCheckModelSummary,
  IDeepfakeScanResult,
  MAX_DEEPFAKE_CLIP_SECONDS,
  MAX_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS,
  MIN_DEEPFAKE_CLIP_SECONDS,
  MIN_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS,
  secondsSetting
} from "../models";
import { AppConfigService } from "./app.config.service";
import { ConferenceService } from "./conference.service";
import {
  DEEPFAKE_DRAIN_MAX_ATTEMPTS,
  DeepfakeApiService,
  DeepfakeDetectionFields,
  DeepfakeMediaParts,
  IDeepfakeSubmitResult,
  IDeepfakeTaskState,
  detectionForm,
  drainRetryDelayMs,
  reasonForHttpError
} from "./deepfake-api.service";
import {
  DeepfakeCaptureCancelledError,
  DeepfakeCaptureService,
  pickAudioMimeType,
  pickClipMimeType
} from "./deepfake-capture.service";
import { GenericErrorHandler } from "./error-handlers.service";
import { AuviousRtcService } from "./rtc.service";
import { UserService } from "./user.service";
import { VoiceDetectionService } from "./voice-detection.service";
import { debug, debugError } from "./utils";

export {
  isMediaRecorderAvailable,
  pickAudioMimeType,
  pickClipMimeType
} from "./deepfake-capture.service";

export interface IDeepfakeEventPayload {
  id: string;
  type: AIDeepfakeEvent;
  timestamp: string;
  payload?: {
    status?: DeepfakeStatusEnum;
    score?: number | null;
    models?: IDeepfakeCheckModelSummary[];
    results?: IDeepfakeScanResult[];
    reason?: DeepfakeFailureReasonEnum;
    mediaKind?: DeepfakeCheckKind;
    targetUserId?: string;
    targetUserEndpointId?: string;
    targetStreamId?: string;
    /**
     * ISO 8601: the record's last persisted change, strictly increasing with
     * every change. Absent from an older ai.
     */
    updatedAt?: string;
  };
}

/** Where a check's media comes from and who asked for it. */
export interface IDeepfakeCheckOptions {
  /** The participant's stream the tile shows; one badge per stream. */
  streamId?: string;
  /** A screen share: never record its audio, even when a tab share carries some. */
  isScreen?: boolean;
  trigger?: DeepfakeTrigger;
}

export interface IDeepfakeCheckContext {
  interactionId?: string;
  conferenceId?: string;
}

/**
 * What the configured check can actually capture from a given stream.
 *
 * A screen has no voice, so its audio (a tab share can carry system audio)
 * is never part of a check; a second camera has no microphone track; an
 * audio-only participant has no frame. `image+audio` degrades to whichever
 * half exists. `null` means there is nothing to check on this stream.
 */
export function kindsFor(
  mode: DeepfakeCheckMode,
  stream: MediaStream | null | undefined,
  isScreen = false
): DeepfakeCheckKind | null {
  const hasVideo = !!stream?.getVideoTracks?.().length;
  const hasAudio = !isScreen && !!stream?.getAudioTracks?.().length;

  switch (mode) {
    case "video":
      return hasVideo ? "video" : null;
    case "audio":
      return hasAudio ? "audio" : null;
    case "image+audio":
      if (hasVideo && hasAudio) return "image+audio";
      if (hasVideo) return "image";
      return hasAudio ? "audio" : null;
    default:
      return hasVideo ? "image" : null;
  }
}

const TASK_STATE_EVENTS: Record<IDeepfakeTaskState["event"], AIDeepfakeEvent> =
  {
    processing: "AIDeepfakeProcessingEvent",
    created: "AIDeepfakeCreatedEvent",
    failed: "AIDeepfakeFailedEvent"
  };

/**
 * How long, and for how many tasks, an event that beat its POST response is
 * kept. The response is normally a round trip away; a minute covers a slow
 * one, and the cap bounds what events for tasks this page will never own
 * (another agent's, the last call's) can hold on to.
 */
const EARLY_EVENT_TTL_MS = 60_000;
const EARLY_EVENT_CAP = 50;

/**
 * How often the conference's checks are listed while in a call: often while
 * another agent's check is still running (its verdict reaches this page only
 * this way), rarely otherwise (a check another agent starts, a late verdict
 * for a timed-out check whose event this page missed).
 */
export const DEEPFAKE_SYNC_PENDING_MS = 10_000;
export const DEEPFAKE_SYNC_IDLE_MS = 30_000;

/**
 * How far along a check is, for deciding whether a listed state is news.
 * Steps before the ai has the media < analysing < timed out < settled. A
 * timed-out check can still settle (the ai keeps asking after the timeout);
 * a settled one changes again only for a newer settled snapshot (see
 * `propagateEvent`).
 */
function progressOfCheck(check: IDeepfakeCheck): number {
  switch (check.state) {
    case "analysing":
      return 1;
    case "timeout":
      return 2;
    case "verdict":
    case "error":
      return 3;
    default:
      return 0;
  }
}

/** The same scale for an ai event, live, replayed or listed. */
function progressOfEvent(
  event: Pick<IDeepfakeEventPayload, "type" | "payload">
): number {
  switch (event.type) {
    case "AIDeepfakeProcessingEvent":
      return 1;
    case "AIDeepfakeFailedEvent":
      return event.payload?.reason === DeepfakeFailureReasonEnum.pollTimeout
        ? 2
        : 3;
    case "AIDeepfakeCreatedEvent":
      return 3;
    default:
      return -1;
  }
}

/**
 * Whether a snapshot of a check (an event, a listed state) is strictly newer
 * than the one the check shows, by the ai's `updatedAt`. Unknown — either
 * side without one (an older ai), or one that is not a date — is not newer.
 */
function isNewerSnapshot(
  event: Pick<IDeepfakeEventPayload, "payload">,
  check: IDeepfakeCheck
): boolean {
  const incoming = Date.parse(event.payload?.updatedAt ?? "");
  const stored = Date.parse(check.updatedAt ?? "");

  return Number.isFinite(incoming) && Number.isFinite(stored)
    ? incoming > stored
    : false;
}

@Injectable()
export class DeepfakeService {
  private config = inject(AppConfigService);
  private user = inject(UserService);
  private rtc = inject(AuviousRtcService);
  private logger = inject(GenericErrorHandler);
  private api = inject(DeepfakeApiService);
  private conference = inject(ConferenceService);
  private voiceDetection = inject(VoiceDetectionService);
  private capture = inject(DeepfakeCaptureService);

  /** ai task id -> local check id. */
  private requestToCheck = new Map<string, string>();

  /**
   * ai events for a task no check carries yet: a fast failure (or even the
   * "processing" event) can arrive before the POST that created the task has
   * answered with its id, and after a reload a live event can come before
   * the list that brings its check back. Replayed once a check carries it.
   */
  private earlyEvents = new Map<
    string,
    { at: number; events: IDeepfakeEventPayload[] }
  >();

  /**
   * Bumped on `reset`, so an answer for the call that was left is dropped,
   * and a check of that call that is still being captured never submits.
   */
  private call = 0;
  /** Emits on `reset`: ends a voice wait of the call that was left. */
  private callLeft$ = new Subject<void>();

  private _checks = signal<{ [id: string]: IDeepfakeCheck }>({});
  public readonly checks = this._checks.asReadonly();
  public readonly pendingCount = computed(
    () =>
      Object.values(this._checks()).filter((check) =>
        DEEPFAKE_PENDING_STATES.includes(check.state)
      ).length
  );

  /** Whether the conference's checks are being followed (see `sync`). */
  private syncing = false;
  /** A list request is out; the next one is scheduled when it answers. */
  private syncInFlight = false;
  /** Asked to list again while a request was out: do so right after it. */
  private syncAgain = false;
  private syncTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    // Joining (and rejoining: a reload, a reconnect) loads the conference's
    // checks from the ai, the only record of them, and keeps following them.
    this.conference.endpointJoined$
      .pipe(
        filter(
          (endpoint) =>
            !!endpoint?.endpoint &&
            endpoint.endpoint === this.rtc.myself?.endpoint
        )
      )
      .subscribe(() => this.startSync());

    // The conference emits our own endpoint as Left when it is destroyed:
    // the call is over, so is its state.
    this.conference.endpointStateChanged$
      .pipe(
        filter(
          (change) =>
            change?.state === EndpointStateEnum.Left &&
            change.endpoint === this.rtc.myself?.endpoint
        )
      )
      .subscribe(() => this.reset());
  }

  /**
   * Forgets every check of the call being left and stops following the
   * conference. This service outlives the call, so without it the next
   * conference would show the previous call's checks.
   */
  public reset(): void {
    this.call++;
    this.callLeft$.next();
    // a recording of the call being left is never submitted: stop it now
    this.capture.cancelAll();
    this.stopSync();
    this._checks.set({});
    this.requestToCheck.clear();
    this.earlyEvents.clear();
    this.syncInFlight = false;
  }

  /** The user id the ai records as `createdBy` for this page's requests. */
  private get me(): string | undefined {
    return this.rtc.myself?.username;
  }

  /**
   * Whether this page gets the check's MQTT events: it submitted it, or the
   * same user did (events go to the requesting user, on any page). Any other
   * check is another agent's, or one the ai answered `existing` for, and is
   * followed by listing only.
   */
  private isOwn(check: IDeepfakeCheck): boolean {
    return (
      (!!check.taskId && this.requestToCheck.has(check.taskId)) ||
      (!!check.createdBy && check.createdBy === this.me)
    );
  }

  /**
   * A check this page only learns about by listing, still running: listed
   * every 10 s, whether its submitter is still in the call or not (the ai
   * sends its events to the submitter only). A timed-out one is not
   * running: a late verdict from the ai's re-check can take hours, so it is
   * picked up on the 30 s cadence, which goes on as long as the call does.
   */
  private get hasForeignPending(): boolean {
    return Object.values(this._checks()).some(
      (check) =>
        !!check.taskId &&
        DEEPFAKE_PENDING_STATES.includes(check.state) &&
        !this.isOwn(check)
    );
  }

  /** A check of this page that has no task id yet: its POST has not answered. */
  private get hasSubmitInFlight(): boolean {
    return Object.values(this._checks()).some(
      (check) => !check.taskId && DEEPFAKE_PENDING_STATES.includes(check.state)
    );
  }

  private startSync(): void {
    if (!this.isEnabled) return;

    this.syncing = true;
    this.syncNow();
  }

  private stopSync(): void {
    this.syncing = false;
    this.syncAgain = false;

    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = undefined;
    }
  }

  /** Lists now, or right after the request that is out: never two at once. */
  private syncNow(): void {
    if (!this.syncing) return;

    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = undefined;
    }

    if (this.syncInFlight) {
      this.syncAgain = true;
      return;
    }

    void this.sync();
  }

  private scheduleSync(): void {
    if (!this.syncing) return;

    const delay = this.syncAgain
      ? 0
      : this.hasForeignPending
        ? DEEPFAKE_SYNC_PENDING_MS
        : DEEPFAKE_SYNC_IDLE_MS;

    this.syncAgain = false;
    this.syncTimer = setTimeout(() => {
      this.syncTimer = undefined;
      void this.sync();
    }, delay);
  }

  /**
   * Lists the conference's checks and merges them in. Only while in a
   * conference with the feature on; a failure (the network, a 403) is logged
   * and the next tick tries again — it is never shown to the agent.
   */
  private async sync(): Promise<void> {
    if (!this.syncing) return;

    if (!this.isEnabled || !this.conference.sessionExists) {
      this.stopSync();
      return;
    }

    const conferenceId = this.conference.session?.conference?.name;

    if (!conferenceId || !this.api.isReady) {
      this.scheduleSync();
      return;
    }

    const call = this.call;

    this.syncInFlight = true;

    try {
      const answer = await this.api.list(conferenceId);

      // the call was left while the request was out: the answer is stale
      if (call !== this.call) return;

      this.merge(answer?.items || []);
    } catch (ex) {
      debug("deepfake: could not list the conference's checks", ex);
    } finally {
      if (call === this.call) {
        this.syncInFlight = false;
        this.scheduleSync();
      }
    }
  }

  /**
   * Turns listed checks into local ones through the path a live event takes
   * (`propagateEvent`). A listed state is a snapshot that may be older than
   * what this page already shows — a live event that came while the request
   * was out, a step taken in this browser — so, like every event, it is
   * applied only when it takes the check further (see `progressOfCheck`),
   * or replaces a settled state with a newer settled one (`updatedAt`).
   */
  private merge(items: IDeepfakeTaskState[]): void {
    const seen = new Set<string>();

    for (const item of items) {
      const taskId = item?.taskId;
      const type = TASK_STATE_EVENTS[item?.event];

      if (!taskId || !type || seen.has(taskId)) continue;

      seen.add(taskId);

      const event: IDeepfakeEventPayload = {
        id: taskId,
        type,
        timestamp: item.createdAt || new Date().toISOString(),
        payload: item.payload
      };

      const checkId = this.checkIdForTask(taskId);

      if (checkId) {
        const check = this._checks()[checkId];

        if (item.createdBy && !check.createdBy) {
          this.patchCheck(checkId, { createdBy: item.createdBy });
        }

        // applied only if it takes the check further (see propagateEvent)
        this.propagateEvent(event);

        continue;
      }

      // This page's own check whose POST has not answered yet: the ai wrote
      // the record first. Held like an event that beat the POST, so the
      // answer finds it, rather than shown twice.
      if (item.createdBy === this.me && this.hasSubmitInFlight) {
        this.keepEarlyEvent(event);
        continue;
      }

      this._checks.set({
        ...this._checks(),
        [taskId]: {
          id: taskId,
          taskId,
          createdBy: item.createdBy,
          targetEndpointId: item.payload?.targetUserEndpointId,
          targetStreamId: item.payload?.targetStreamId,
          trigger: item.trigger,
          mediaKind: item.payload?.mediaKind,
          state: "analysing",
          createdAt: item.createdAt || new Date().toISOString()
        }
      });

      // live events that came before the list did (a reload), then the
      // listed state if it is further along
      this.replayEarlyEvents(taskId);
      this.propagateEvent(event);
    }
  }

  /** Keeps an event for a task no check carries yet (see `earlyEvents`). */
  private keepEarlyEvent(event: IDeepfakeEventPayload): void {
    const now = Date.now();

    for (const [taskId, entry] of this.earlyEvents) {
      if (now - entry.at > EARLY_EVENT_TTL_MS) this.earlyEvents.delete(taskId);
    }

    const entry = this.earlyEvents.get(event.id);

    if (entry) {
      entry.events.push(event);
      return;
    }

    if (this.earlyEvents.size >= EARLY_EVENT_CAP) {
      // insertion order: the first key is the oldest
      this.earlyEvents.delete(this.earlyEvents.keys().next().value);
    }

    this.earlyEvents.set(event.id, { at: now, events: [event] });
  }

  /**
   * Applies the events kept for a task, now that a check carries it, in the
   * order they came. A terminal event that overtook "processing" on the way
   * in still has the last word: `propagateEvent` never lets "processing"
   * reopen a settled check.
   */
  private replayEarlyEvents(taskId: string): void {
    const entry = this.earlyEvents.get(taskId);

    if (!entry || !this.checkIdForTask(taskId)) return;

    this.earlyEvents.delete(taskId);

    if (Date.now() - entry.at > EARLY_EVENT_TTL_MS) return;

    entry.events.forEach((event) => this.propagateEvent(event));
  }

  /** Same guard shape as the vision feature: only offered for the customer. */
  public supported(endpoint: IEndpoint): boolean {
    const roles = endpoint?.metadata?.roles || [];
    return roles.includes(UserRoleEnum.customer);
  }

  public get isEnabled(): boolean {
    return (
      this.user.isAgent &&
      this.config.agentParamEnabled(AgentParam.DEEPFAKE_DETECTION_ENABLED)
    );
  }

  /** Video checks additionally need a working MediaRecorder. */
  public get isVideoCheckSupported(): boolean {
    return !!pickClipMimeType();
  }

  /**
   * The check the admin configured for this application. An unknown value
   * (a newer config than this build) falls back to the default instead of
   * hiding the action.
   */
  public get checkMode(): DeepfakeCheckMode {
    const mode = this.config.agentParam(AgentParam.DEEPFAKE_CHECK);

    return DEEPFAKE_CHECK_MODES.includes(mode)
      ? mode
      : DEFAULT_DEEPFAKE_CHECK_MODE;
  }

  /** Audio checks need MediaRecorder with an audio-only container. */
  public get isAudioCheckSupported(): boolean {
    return !!pickAudioMimeType();
  }

  /**
   * How long a clip check records for, in ms: the admin's seconds, clamped;
   * the default when the config holds nothing usable, so a garbage value can
   * never stop the recorder at once or never.
   */
  public get clipDurationMs(): number {
    return (
      secondsSetting(
        this.config.agentParam(AgentParam.DEEPFAKE_CLIP_SECONDS),
        MIN_DEEPFAKE_CLIP_SECONDS,
        MAX_DEEPFAKE_CLIP_SECONDS,
        DEFAULT_DEEPFAKE_CLIP_SECONDS
      ) * 1000
    );
  }

  /** Whether a clip check holds its recording until the participant is heard. */
  public get startsClipOnVoice(): boolean {
    return this.config.agentParamEnabled(AgentParam.DEEPFAKE_CLIP_ON_VOICE);
  }

  /** How long, in ms, a clip check waits for a voice before recording anyway. */
  public get voiceWaitMs(): number {
    return (
      secondsSetting(
        this.config.agentParam(AgentParam.DEEPFAKE_CLIP_VOICE_WAIT_SECONDS),
        MIN_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS,
        MAX_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS,
        DEFAULT_DEEPFAKE_CLIP_VOICE_WAIT_SECONDS
      ) * 1000
    );
  }

  /** Whether this browser can perform the configured check at all. */
  public get isCheckSupported(): boolean {
    return this.isKindSupported(this.checkMode);
  }

  private isKindSupported(kind: DeepfakeCheckKind): boolean {
    switch (kind) {
      case "video":
        return this.isVideoCheckSupported;
      case "audio":
      case "image+audio":
        return this.isAudioCheckSupported;
      default:
        return true;
    }
  }

  /**
   * Whether the configured check has anything to capture from a stream of
   * this shape. A screen share under an audio-only check has not.
   */
  public canCheckStream(isScreen: boolean): boolean {
    return !(isScreen && this.checkMode === "audio");
  }

  /**
   * Runs the configured check on the media the tile's element plays, or as
   * much of it as the stream has (see `kindsFor`). Returns the local check
   * id; rejects, without creating a check, when there is nothing to check.
   * Once the check exists, a failure is recorded on it and this still resolves.
   */
  public check(
    element: HTMLMediaElement,
    target: IEndpoint,
    context: IDeepfakeCheckContext = {},
    options: IDeepfakeCheckOptions = {}
  ): Promise<string> {
    const source = element?.srcObject;
    const kind = kindsFor(
      this.checkMode,
      source instanceof MediaStream ? source : null,
      options.isScreen
    );

    if (!kind) {
      return Promise.reject(
        new Error("The participant's stream has no media this check can use")
      );
    }

    if (!this.isKindSupported(kind)) {
      return Promise.reject(new Error("This browser cannot record a clip"));
    }

    const videoElement = element as HTMLVideoElement;

    switch (kind) {
      case "video":
        return this.checkVideo(videoElement, target, context, options);
      case "audio":
        return this.checkAudio(element, target, context, options);
      case "image+audio":
        return this.checkImageAndAudio(videoElement, target, context, options);
      default:
        return this.checkImage(videoElement, target, context, options);
    }
  }

  public getChecksForEndpoint(endpointId: string): IDeepfakeCheck[] {
    return Object.values(this._checks())
      .filter((check) => check.targetEndpointId === endpointId)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }

  public getLatestCheckForEndpoint(
    endpointId: string
  ): IDeepfakeCheck | undefined {
    return this.getChecksForEndpoint(endpointId)[0];
  }

  /**
   * The badge is per stream: a participant's camera, second camera and screen
   * each carry their own. A check from an older client has no stream and
   * matches by participant instead, so it is not lost.
   */
  public matchesStream(
    check: IDeepfakeCheck,
    endpointId: string,
    streamId?: string
  ): boolean {
    if (check.targetEndpointId !== endpointId) return false;

    return !streamId || !check.targetStreamId
      ? true
      : check.targetStreamId === streamId;
  }

  /** Reads the `checks` signal, so a `computed` over it follows every change. */
  public getLatestCheckForStream(
    endpointId: string,
    streamId?: string
  ): IDeepfakeCheck | undefined {
    return this.getChecksForEndpoint(endpointId).find((check) =>
      this.matchesStream(check, endpointId, streamId)
    );
  }

  private setCheck(check: IDeepfakeCheck) {
    this._checks.set({ ...this._checks(), [check.id]: check });
  }

  private patchCheck(id: string, changes: Partial<IDeepfakeCheck>) {
    const existing = this._checks()[id];
    if (!existing) return;

    this.setCheck({ ...existing, ...changes });
  }

  /**
   * Captures a still from the participant's tile and submits it.
   * Returns the local check id.
   */
  public async checkImage(
    videoElement: HTMLVideoElement,
    target: IEndpoint,
    context: IDeepfakeCheckContext = {},
    options: IDeepfakeCheckOptions = {}
  ): Promise<string> {
    return this.submit("image", target, context, options, async (id) => {
      this.startedRecording(id);

      return { image: await this.capture.captureFrame(videoElement) };
    });
  }

  /**
   * Records a short clip from the participant's inbound stream and submits it.
   *
   * NOTE: video scans need a Reality Defender plan that covers video. On a plan
   * that does not, this comes back as a distinct "plan" failure — never as a
   * verdict.
   */
  public async checkVideo(
    videoElement: HTMLVideoElement,
    target: IEndpoint,
    context: IDeepfakeCheckContext = {},
    options: IDeepfakeCheckOptions = {}
  ): Promise<string> {
    return this.submit("video", target, context, options, (id) =>
      this.captureClip(
        id,
        videoElement,
        options,
        async (stream) => {
          const video = await this.capture.recordClip(
            stream,
            this.clipDurationMs
          );

          return { video };
        },
        { pictureOnlyOnScreen: true }
      )
    );
  }

  /**
   * Records a short audio-only clip of the participant and submits it.
   * Audio is on Reality Defender's free tier. Silence, music, a second
   * speaker or a too-short clip come back as NOT_APPLICABLE with a reason.
   */
  public async checkAudio(
    element: HTMLMediaElement,
    target: IEndpoint,
    context: IDeepfakeCheckContext = {},
    options: IDeepfakeCheckOptions = {}
  ): Promise<string> {
    return this.submit("audio", target, context, options, (id) =>
      this.captureClip(id, element, options, async (stream) => {
        const audio = await this.capture.recordAudio(
          stream,
          this.clipDurationMs
        );

        return { audio };
      })
    );
  }

  /**
   * One check, two scans: a still taken as the recording starts, plus the
   * audio clip. Both go in one request so the agent gets one badge with a
   * line per scan. Costs two Reality Defender scans.
   */
  public async checkImageAndAudio(
    videoElement: HTMLVideoElement,
    target: IEndpoint,
    context: IDeepfakeCheckContext = {},
    options: IDeepfakeCheckOptions = {}
  ): Promise<string> {
    return this.submit("image+audio", target, context, options, (id) =>
      this.captureClip(id, videoElement, options, async (stream) => {
        // start recording first so the still is from the same moment the
        // audio starts; awaited together, so a recording cancelled (or
        // failed) while the still is taken is never an unhandled rejection
        const recording = this.capture.recordAudio(stream, this.clipDurationMs);
        const [audio, image] = await Promise.all([
          recording,
          this.capture.captureFrame(videoElement)
        ]);

        return { image, audio };
      })
    );
  }

  /**
   * The capture every clip check shares: the participant's stream, the voice
   * wait, then the recording itself, which `record` performs and turns into
   * the request fields. With `pictureOnlyOnScreen`, a screen share's stream
   * is cut to its picture before anything listens to or records it.
   */
  private async captureClip(
    id: string,
    element: HTMLMediaElement,
    options: IDeepfakeCheckOptions,
    record: (stream: MediaStream) => Promise<DeepfakeMediaParts>,
    { pictureOnlyOnScreen = false } = {}
  ): Promise<DeepfakeMediaParts> {
    const call = this.call;
    const source = this.remoteStreamOf(element);
    // a screen has no voice: a tab share's system audio is not the participant
    const stream =
      pictureOnlyOnScreen && options.isScreen
        ? new MediaStream(source.getVideoTracks())
        : source;

    await this.awaitVoice(id, options.streamId, stream);

    // the call was left while waiting for a voice: record nothing
    if (call !== this.call) throw new DeepfakeCaptureCancelledError();

    this.startedRecording(id);

    return record(stream);
  }

  /**
   * Holds a clip check until the participant is heard speaking, so the
   * recording window opens on a voice rather than on a quiet moment. Only
   * with the option on and only when the stream to record carries audio (a
   * screen share never does); the tile's voice detection is the ear, and the
   * admin's wait is the bound, after which the clip records regardless.
   */
  private async awaitVoice(
    checkId: string,
    streamId: string | undefined,
    stream: MediaStream
  ): Promise<void> {
    if (
      !this.startsClipOnVoice ||
      !streamId ||
      !stream.getAudioTracks().length
    ) {
      return;
    }

    this.patchCheck(checkId, { state: "listening" });

    await firstValueFrom(
      this.voiceDetection.volumeLevel$.pipe(
        filter((volume) => volume.streamId === streamId && volume.speaking),
        map(() => undefined),
        timeout({ first: this.voiceWaitMs, with: () => of(undefined) }),
        takeUntil(this.callLeft$)
      ),
      { defaultValue: undefined }
    );
  }

  /**
   * Marks the moment the capture itself starts, so the badge stops saying it
   * is waiting for a voice the moment it no longer is. Called on every path,
   * including the one where `awaitVoice` returned without listening at all.
   */
  private startedRecording(checkId: string): void {
    this.patchCheck(checkId, { state: "recording" });
  }

  private remoteStreamOf(element: HTMLMediaElement): MediaStream {
    const source = element?.srcObject;

    if (!(source instanceof MediaStream)) {
      throw new Error("The participant's stream is not available to record");
    }

    return source;
  }

  private async submit(
    mediaKind: DeepfakeCheckKind,
    target: IEndpoint,
    context: IDeepfakeCheckContext,
    options: IDeepfakeCheckOptions,
    capture: (checkId: string) => Promise<DeepfakeMediaParts>
  ): Promise<string> {
    const id = Util.uuidgen();
    const trigger = options.trigger ?? "agent";
    // the call this check belongs to: once it is left (`reset`), nothing of
    // the check goes on — no upload (a paid scan nobody sees), no answer
    const call = this.call;

    this.setCheck({
      id,
      targetEndpointId: target?.endpoint,
      targetStreamId: options.streamId,
      trigger,
      state: "submitted",
      mediaKind,
      createdAt: new Date().toISOString()
    });

    let media: DeepfakeMediaParts;

    try {
      media = await capture(id);
    } catch (ex) {
      // the call was left: the capture was cancelled, nothing to report
      if (call !== this.call) return id;

      // capturing a still or recording a clip failed locally — that is not a
      // network problem and definitely not a verdict
      debugError(ex);

      this.patchCheck(id, {
        state: "error",
        reason: DeepfakeFailureReasonEnum.mediaPreparationFailed
      });

      return id;
    }

    if (call !== this.call) return id;

    this.patchCheck(id, { state: "uploading" });

    try {
      const response = await this.post(media, call, {
        interactionId: context.interactionId,
        conferenceId: context.conferenceId,
        targetUserId: target?.username,
        targetUserEndpointId: target?.endpoint,
        targetStreamId: options.streamId,
        trigger
      });

      // the call was left while the upload (or a wait to retry it) was out:
      // nothing shows it now
      if (!response || call !== this.call) return id;

      const listed = this.checkIdForTask(response.id);

      if (listed && listed !== id && !this.requestToCheck.has(response.id)) {
        // the list brought this task in while the POST was out (an automatic
        // check another agent already made): one badge, the listed one
        const { [id]: _local, ...rest } = this._checks();
        this._checks.set(rest);

        if (!response.existing) this.requestToCheck.set(response.id, listed);

        return listed;
      }

      if (response.existing) {
        // an automatic check of this stream already exists: the ai started
        // nothing and sends this page no events for it, so it is followed
        // by listing, starting now
        this.patchCheck(id, { taskId: response.id, state: "analysing" });
        this.syncNow();

        return id;
      }

      // the ai service task id is what the MQTT events carry; it also goes on
      // the check, so the listed state of it after a reload matches too
      this.requestToCheck.set(response.id, id);
      this.patchCheck(id, { taskId: response.id });
      this.replayEarlyEvents(response.id);
    } catch (ex) {
      if (call !== this.call) return id;

      debugError(ex);

      // Settle the check BEFORE handing the error on: handleNotAuthenticatedError
      // rethrows anything that is not a connection error, and a rethrow here
      // would leave the badge spinning on "submitted" forever.
      this.patchCheck(id, {
        state: "error",
        reason: reasonForHttpError(ex)
      });

      // ...and do not let that rethrow reject the check either: the badge
      // already says why, so a rejection would pop the same failure up twice
      try {
        this.logger.handleNotAuthenticatedError?.(ex);
      } catch {
        // reported on the check above
      }
    }

    return id;
  }

  /**
   * The detection POST, retried while it meets a draining ai pod: that pod
   * answers 503 `SERVICE_DRAINING` with a `Retry-After`, and the next attempt
   * reaches another pod through the load balancer. Manual and automatic
   * checks alike; at most `DEEPFAKE_DRAIN_MAX_ATTEMPTS` POSTs, then the last
   * error is thrown (shown as "busy, try again shortly"). Any other error is
   * thrown at once. Resolves `undefined` when the call is left during a wait:
   * nothing is sent for a call that is over.
   */
  private async post(
    media: DeepfakeMediaParts,
    call: number,
    fields: DeepfakeDetectionFields
  ): Promise<IDeepfakeSubmitResult | undefined> {
    for (let attempt = 1; ; attempt++) {
      try {
        return await this.api.submit(detectionForm(media, fields));
      } catch (ex) {
        const delay = drainRetryDelayMs(ex);

        if (
          delay === undefined ||
          attempt >= DEEPFAKE_DRAIN_MAX_ATTEMPTS ||
          call !== this.call
        ) {
          throw ex;
        }

        debug("deepfake: the ai pod is draining, retrying in", delay, "ms");

        const left = await firstValueFrom(
          timer(delay).pipe(
            map(() => false),
            takeUntil(this.callLeft$)
          ),
          { defaultValue: true }
        );

        if (left || call !== this.call) return undefined;
      }
    }
  }

  /**
   * The local check an ai event belongs to. The map covers checks submitted
   * from this page; after a reload it is empty, but the checks come back from
   * the ai's list carrying their `taskId`.
   */
  private checkIdForTask(taskId: string): string | undefined {
    return (
      this.requestToCheck.get(taskId) ??
      Object.values(this._checks()).find((check) => check.taskId === taskId)
        ?.id
    );
  }

  public propagateEvent(event: IDeepfakeEventPayload) {
    const checkId = this.checkIdForTask(event.id);

    if (!checkId) {
      // most likely the POST that created the task has not answered yet
      this.keepEarlyEvent(event);
      return;
    }

    const check = this._checks()[checkId];

    if (!check) return;

    const progress = progressOfEvent(event);
    const settled = progressOfCheck(check) === 3;

    // Only forward: a late, duplicated or reordered event (a "processing"
    // after the verdict, a poll timeout that overtook nothing) never takes a
    // check back. A timed-out check still settles: the ai keeps asking after
    // the timeout, and its late verdict is further along.
    //
    // A settled check changes once more only for a settled snapshot the ai
    // persisted later (a strictly newer `updatedAt`): the ai's re-check of a
    // scan that timed out (the audio half of an image+audio check, say)
    // settles it and publishes the check again, possibly with another
    // headline. An older or equal snapshot, or one without `updatedAt` on
    // either side (an older ai), never replaces it.
    const replacesSettled =
      settled && progress === 3 && isNewerSnapshot(event, check);

    if (progress <= progressOfCheck(check) && !replacesSettled) return;

    const updatedAt = event.payload?.updatedAt;

    switch (event.type) {
      case "AIDeepfakeProcessingEvent":
        this.patchCheck(checkId, { state: "analysing", updatedAt });
        break;
      case "AIDeepfakeCreatedEvent":
        // every field of the snapshot replaced whole, so the headline and
        // the per-scan lines come from the newest results only
        this.patchCheck(checkId, {
          state: "verdict",
          status: event.payload?.status,
          score: event.payload?.score ?? null,
          models: event.payload?.models || [],
          results: event.payload?.results || [],
          reason: undefined,
          updatedAt
        });
        break;
      case "AIDeepfakeFailedEvent":
        this.patchCheck(checkId, {
          // a poll timeout is not the same thing as "could not evaluate"
          state:
            event.payload?.reason === DeepfakeFailureReasonEnum.pollTimeout
              ? "timeout"
              : "error",
          reason: event.payload?.reason || DeepfakeFailureReasonEnum.unknown,
          results: event.payload?.results || [],
          status: undefined,
          score: undefined,
          models: undefined,
          updatedAt
        });
        break;
    }
  }
}
